/**
 * Where a player says they live, and the vocabulary for asking.
 *
 * ── WHY THE LIST IS DERIVED RATHER THAN WRITTEN DOWN ─────────────────────────
 *
 * A residence country is a LICENCE-relevant field on this platform: PAGCOR's
 * offshore regime permits serving players outside the Philippines and prohibits
 * Philippine residents, and its domestic PIGO regime is the reverse. So the set
 * of codes the server accepts and the set the player can pick from must be the
 * same set — a picker offering a code the validator refuses is a form nobody can
 * submit, and a validator accepting a code the picker never offered is a field
 * that can be filled with anything by anyone bypassing the form.
 *
 * Both come from `Intl.DisplayNames`, which ships with the runtime on the server
 * and in the browser, so there is one source and no copy to drift. A frozen
 * 250-line table maintained by hand is a table that is wrong the first time a
 * territory changes and nobody notices, and it would need maintaining in two
 * places to be useful for both validation and display.
 *
 * The set is what ICU can NAME as a region, which is slightly wider than ISO
 * 3166-1's assigned country codes — it includes exceptionally-reserved entries
 * such as AC (Ascension Island). That is deliberate and harmless here: this
 * field records where a person says they live, and being able to say "Ascension
 * Island" costs nothing, while refusing a real territory because a hand-written
 * list omitted it costs a customer.
 */
import { z } from 'zod';

/** Names a region code in English. `fallback: 'none'` so an unknown code is undefined. */
const REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' });

/**
 * Codes CLDR happily NAMES that nobody can live in.
 *
 * "Is it nameable?" turned out to be a weaker test than it looks, and a test
 * caught it: `ZZ` is named "Unknown Region", which would have been stored as a
 * player's country of residence and then read back by a licence check as though
 * it meant something. The others are organisations, currency areas, groupings
 * and the pseudo-locales bundlers use for translation testing — every one of
 * them a real answer from `Intl` and none of them a place a person lives.
 *
 * `AN`, `CS` and `YU` are the opposite problem and are deliberately NOT here:
 * they are withdrawn codes that CLDR still resolves to their successors
 * (Curaçao, Serbia). Accepting them costs nothing and refusing them would
 * reject somebody whose old paperwork uses one.
 */
const NOT_A_PLACE_OF_RESIDENCE = new Set([
  'ZZ', // Unknown Region — the literal absence of an answer
  'EU', // European Union
  'EZ', // Eurozone
  'UN', // United Nations
  'QO', // Outlying Oceania — a grouping, not a territory
  'XA', // Pseudo-Accents, a translation-testing locale
  'XB', // Pseudo-Bidi, likewise
]);

/** The English name of a region code, or null when the runtime cannot name it. */
export function countryName(code: string): string | null {
  if (!/^[A-Za-z]{2}$/.test(code)) return null;
  const upper = code.toUpperCase();
  if (NOT_A_PLACE_OF_RESIDENCE.has(upper)) return null;
  const name = REGION_NAMES.of(upper);
  // ICU echoes the input back for codes it does not know when fallback is on;
  // with fallback 'none' it returns undefined. Both are treated as "unknown".
  return !name || name === upper ? null : name;
}

/** Every region this runtime can name, as `{ code, name }`, sorted by name. */
export function listCountries(): { code: string; name: string }[] {
  const out: { code: string; name: string }[] = [];
  for (let first = 65; first <= 90; first += 1) {
    for (let second = 65; second <= 90; second += 1) {
      const code = String.fromCharCode(first) + String.fromCharCode(second);
      const name = countryName(code);
      if (name) out.push({ code, name });
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name, 'en'));
}

/**
 * A residence country as the platform stores it: ISO 3166-1 alpha-2, upper case.
 *
 * Two checks, not one. The shape check mirrors the database's own
 * `player_residence_country_iso` CHECK so the two cannot disagree about what a
 * code looks like; the nameable check is what stops "ZZ" — well-shaped,
 * refused by nothing, and meaningless to every downstream question the column
 * exists to answer.
 */
export const residenceCountry = z
  .string()
  .trim()
  .toUpperCase()
  .pipe(
    z
      .string()
      .regex(/^[A-Z]{2}$/, 'Choose a country from the list.')
      .refine((code) => countryName(code) !== null, 'That is not a country we recognise.'),
  );

/**
 * How a brand reads its country list. Mirrors `player.residency_mode` and is
 * evaluated by `player.residency_permitted` in SQL — this type exists so the
 * console cannot offer a fourth mode the database has never heard of.
 */
export const RESIDENCY_MODES = ['ACCEPT_ALL', 'ALLOW_LIST', 'BLOCK_LIST'] as const;
export const residencyMode = z.enum(RESIDENCY_MODES);
export type ResidencyMode = (typeof RESIDENCY_MODES)[number];

/** What the sign-up screen needs to render the picker without a second source. */
export const residencyOptionsResult = z.object({
  countries: z.array(z.object({ code: z.string(), name: z.string() })),
});
export type ResidencyOptionsResult = z.infer<typeof residencyOptionsResult>;

/* ═══════════════════════════════════════════════════════════════════════════
   GOVERNING THE RULE, AND SEEING WHO IT APPLIES TO

   Everything above describes ASKING a player where they live. Everything below
   describes the two things the console could not do with the answer: state the
   policy in force and change it, and count the players it applies to.

   The shapes are here rather than in `platform-admin.ts` — which owns the
   generic parameter write — because a residency policy is not a generic
   parameter. It is TWO `platform.setting` rows that only mean anything read
   together, evaluated by a rule this file already names, and the pairing is the
   part that has to be validated. `platform-admin.ts` deliberately does not
   validate a parameter's value at all: `platform.setting.value` is untyped text
   and every reader parses it for itself. That is the right default and the
   wrong answer for the one parameter that decides whether the operator is
   complying with its gambling licence.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * WHAT EACH MODE ACTUALLY DOES, in words the console prints verbatim.
 *
 * Shared rather than written on the screen, because two places must agree about
 * it and one of them is a refusal: `player.residency_permitted` evaluates the
 * mode in SQL, and an operator reading "blocks the countries listed" needs that
 * sentence to be the same sentence the database acts on.
 *
 * `enforces` is the honesty flag. ACCEPT_ALL is not a weaker restriction, it is
 * NO restriction — the platform admits every country there is — and a screen
 * that renders three modes as three shades of the same control invites an
 * operator to believe the switched-off one is doing something.
 */
export const RESIDENCY_MODE_EFFECT: Record<
  ResidencyMode,
  { label: string; effect: string; enforces: boolean }
> = {
  ACCEPT_ALL: {
    label: 'Accept every country',
    effect:
      'No residency restriction is in force. Registration is accepted from a resident of any ' +
      'country, and the country list below is not read at all.',
    enforces: false,
  },
  ALLOW_LIST: {
    label: 'Admit only the countries listed',
    effect:
      'Registration is accepted only from a resident of a listed country and refused from ' +
      'everywhere else. This is the shape a domestic PIGO licence takes.',
    enforces: true,
  },
  BLOCK_LIST: {
    label: 'Refuse the countries listed',
    effect:
      'Registration is refused from a resident of a listed country and accepted from ' +
      'everywhere else. This is the shape an offshore licence takes, with PH listed.',
    enforces: true,
  },
};

/**
 * The two `platform.setting` keys a residency policy IS.
 *
 * Named here so the console, the write route and the tests address the same two
 * rows. They are ordinary parameters as far as `platform.setting` is concerned
 * — which is exactly why the policy screen exists: read as raw text on the
 * generic parameters table, `BLOCK_LIST` and `PH` are two unrelated strings.
 */
export const RESIDENCY_MODE_KEY = 'player.residency_mode';
export const RESIDENCY_COUNTRIES_KEY = 'player.residency_countries';

/**
 * A country list as the platform stores it.
 *
 * Deduplicated and sorted, because the stored form is a single text column that
 * a person reads: `PH,SG,PH` and `SG,PH` are the same policy, and only one of
 * them can be compared against last month's value in the configuration log to
 * see what actually changed.
 *
 * The cap is well above the ~250 codes that exist. It is here so a request
 * cannot make the settings write arbitrarily large, not because a real list
 * approaches it.
 */
export const residencyCountryList = z
  .array(residenceCountry)
  .max(300, 'That is more entries than there are countries — check the list.')
  .transform((codes) => [...new Set(codes)].toSorted());

/**
 * Changing a brand's residency policy.
 *
 * ONE request for BOTH rows, and that is the point of it rather than a
 * convenience. The mode and the list are only meaningful together: moving a
 * brand from ACCEPT_ALL to BLOCK_LIST is two settings writes, and between them
 * the brand sits at BLOCK_LIST with an empty list — a posture that reads as
 * enforcement on every screen and refuses nobody. Sent as one request they land
 * in one transaction, and the database's own deferred check
 * (`sql/330_residency_governance.sql`) judges the PAIR at commit.
 *
 * `brandId` absent means the platform-wide row every brand inherits. Writing it
 * is refused to a brand-scoped administrator by the same rule every other
 * platform-wide write on these screens uses — it is the widest blast radius on
 * the screen, not the narrowest.
 */
export const writeResidencyPolicyRequest = z
  .object({
    brandId: z.string().min(1).optional(),
    mode: residencyMode,
    countries: residencyCountryList.default([]),
  })
  .superRefine((body, ctx) => {
    if (body.mode !== 'ACCEPT_ALL' && body.countries.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['countries'],
        message:
          body.mode === 'ALLOW_LIST'
            ? 'An allow list naming no country admits nobody — every registration on this brand ' +
              'would be refused, and each player told their own country was the problem. Name at ' +
              'least one country, or choose “Accept every country”.'
            : 'A block list naming no country refuses nobody, while every screen would report a ' +
              'restriction. Name at least one country, or choose “Accept every country”, which ' +
              'is the honest way to enforce nothing.',
      });
    }
  });
export type WriteResidencyPolicyRequest = z.infer<typeof writeResidencyPolicyRequest>;

/**
 * The policy in force for one brand, as the console reads it back.
 *
 * `inherited` is not decoration. A brand with no rows of its own follows the
 * platform-wide pair, and an operator who changes the global value while
 * believing they are changing one brand's has changed every brand's. So the
 * screen shows which of the two a value came from, per key, rather than
 * printing an effective value with no provenance.
 */
export const residencyPolicyRow = z.object({
  brandId: z.string().nullable(),
  brandName: z.string().nullable(),
  mode: residencyMode,
  countries: z.array(z.string()),
  /** True when this brand has no `player.residency_mode` row of its own. */
  modeInherited: z.boolean(),
  /** True when this brand has no `player.residency_countries` row of its own. */
  countriesInherited: z.boolean(),
});
export type ResidencyPolicyRow = z.infer<typeof residencyPolicyRow>;

export const residencyPolicyResult = z.object({
  /** The platform-wide pair, which every brand without an override follows. */
  platformWide: residencyPolicyRow,
  /** One row per brand IN THE READER'S SCOPE, resolved. */
  brands: z.array(residencyPolicyRow),
  /**
   * Whether `brands` is EVERY brand, or only the ones this reader may act on.
   *
   * The console uses it to decide how wide it may state the posture. It cannot
   * work this out from the array's length: the brand list on the session is
   * filtered by the same scope, so comparing the two is always equal and the
   * caveat it guards becomes dead code. Only the server knows.
   */
  coversEveryBrand: z.boolean(),
  /** Every country the picker may offer, so the screen needs no second source. */
  countries: z.array(z.object({ code: z.string(), name: z.string() })),
});
export type ResidencyPolicyResult = z.infer<typeof residencyPolicyResult>;

/**
 * "Never asked", as a filter value and as a rendered word.
 *
 * A player who registered before `residence_country` existed has NULL, and NULL
 * is not a country — it is the absence of the question. It needs a value the
 * roster filter can carry in a URL, and it must never be spelled as a code that
 * could collide with one: every ISO 3166-1 alpha-2 code is two letters, so a
 * seven-letter sentinel cannot be mistaken for one.
 */
export const RESIDENCE_UNKNOWN = 'UNKNOWN';

/**
 * What the roster's residence filter accepts: one country, or "never asked".
 *
 * Upper-cased first so a hand-typed `?residenceCountry=ph` works, then matched
 * against the sentinel or validated as a real, nameable country — the SAME
 * check registration applies, so the roster cannot filter on a code the
 * platform would refuse to store.
 */
export const residenceFilter = z
  .string()
  .trim()
  .toUpperCase()
  .pipe(
    z.union([
      z.literal(RESIDENCE_UNKNOWN),
      z
        .string()
        .regex(/^[A-Z]{2}$/, 'Choose a country from the list.')
        .refine((code) => countryName(code) !== null, 'That is not a country we recognise.'),
    ]),
  );

/**
 * How many players live where, brand-scoped.
 *
 * `neverAsked` is its own field rather than a row with a null code, because it
 * is a different kind of fact: the others count answers, and this counts the
 * absence of the question. Folding it in as an unnamed row is how a chart comes
 * to show "unknown" as the largest market.
 */
export const residenceCountRow = z.object({
  code: z.string(),
  name: z.string().nullable(),
  players: z.number().int().nonnegative(),
  /**
   * What `player.residency_permitted` says about this country for this brand
   * TODAY — null on the platform-wide total, where there is no brand to ask
   * about. It is what turns a count into something to act on: players already
   * on the roster whom the policy as it now stands would refuse.
   */
  permitted: z.boolean().nullable(),
});
export type ResidenceCountRow = z.infer<typeof residenceCountRow>;

export const residencySummaryResult = z.object({
  /** One row per country present on the roster, most players first. */
  countries: z.array(residenceCountRow),
  /** Players whose residence was never recorded. Not a country, and not zero. */
  neverAsked: z.number().int().nonnegative(),
  /** Every player in scope, so a share can be computed without a second read. */
  total: z.number().int().nonnegative(),
  /** The brand this was counted for, or null for every brand in scope. */
  brandId: z.string().nullable(),
});
export type ResidencySummaryResult = z.infer<typeof residencySummaryResult>;

/* ── What a player has agreed to be sent ──────────────────────────────────── */

/**
 * Grant or withdraw consent to marketing.
 *
 * A single boolean, deliberately. "Preferences" that offer a grid of channels
 * and categories look thorough and produce a state nobody can reason about at
 * send time; this platform has one marketing channel — email — so there is one
 * question. When a second channel exists, this becomes a shape with two fields
 * and the resolver has something real to read.
 */
export const marketingConsentRequest = z.object({
  granted: z.boolean(),
});
export type MarketingConsentRequest = z.infer<typeof marketingConsentRequest>;

export const marketingConsentResult = z.object({
  granted: z.boolean(),
  grantedAt: z.string().nullable().optional(),
  withdrawnAt: z.string().nullable().optional(),
  /** A confirmed address. Consent alone does not make somebody reachable. */
  emailVerified: z.boolean(),
  /** Both conditions together — what the resolver will actually act on. */
  willReceive: z.boolean(),
});
export type MarketingConsentResult = z.infer<typeof marketingConsentResult>;
